/**
 * Katalog › doporučené materiály podle typu objektu a fáze/milníku.
 *
 * - **Obecný** (`OBECNY`) = master produktový katalog (`components` + Obecné kategorie)
 * - Ostatní scopes = doporučené sady (tabulky `material_recommended_*`);
 *   Excel: **1 soubor = 1 typ objektu**, **listy = fáze/milníky** (soft sync upsert);
 *   jednotlivé SAP: Specifikace › Rozšířené / `PATCH …/recommended-placements`
 */

import { getObjectTypeName } from './object-type-labels.constants';
import {
  BUDOVA_MILESTONE_IDS,
  BUDOVA_MILESTONE_LABELS,
} from './object-workflow/budova-milestone-engine';
import {
  ROZVADEC_MILESTONE_IDS,
  ROZVADEC_MILESTONE_LABELS,
} from './object-workflow/rozvadec-milestone-engine';
import { GENERATED_WORKFLOWS } from './object-workflow/generated-workflows';
import { resolvePhaseLabel } from './object-workflow/phase-codes';
import {
  parseBudovaMilestoneFromAttachmentPhaseKey,
  parseRozvadecMilestoneFromAttachmentPhaseKey,
} from './object-workflow/attachment-phase';
import { sha256Hex, sha256HexToUuidV4Style } from '../utils/sha256-hex.util';
import { normalizeProductCategorySegment } from './material-product-category.constants';

/** Admin scope přepínače Katalogu (Obecný + typy s doporučenými materiály). */
export const MATERIAL_CATALOG_ADMIN_SCOPES = [
  'OBECNY',
  'SPOJKOVISTE',
  'BUDOVA',
  'VYKOP',
  'JAMA',
  'ROZVADEC',
] as const;

export type MaterialCatalogAdminScope = (typeof MATERIAL_CATALOG_ADMIN_SCOPES)[number];

/** Typy objektu s doporučeným katalogem (bez Obecný). */
export type RecommendedCatalogObjectType = Exclude<MaterialCatalogAdminScope, 'OBECNY'>;

export const RECOMMENDED_CATALOG_OBJECT_TYPES: readonly RecommendedCatalogObjectType[] = [
  'SPOJKOVISTE',
  'BUDOVA',
  'VYKOP',
  'JAMA',
  'ROZVADEC',
] as const;

export const MATERIAL_CATALOG_ADMIN_SCOPE_LABELS: Record<MaterialCatalogAdminScope, string> = {
  OBECNY: 'Obecný',
  SPOJKOVISTE: getObjectTypeName('SPOJKOVISTE'),
  BUDOVA: getObjectTypeName('BUDOVA'),
  VYKOP: getObjectTypeName('VYKOP'),
  JAMA: getObjectTypeName('JAMA'),
  ROZVADEC: getObjectTypeName('ROZVADEC'),
};

export interface RecommendedCatalogSheetDef {
  /** Stabilní klíč v DB / API (`phase_key`). */
  key: string;
  /** Název listu v Excelu / popisek v UI. */
  label: string;
}

function uniquePhaseSheetsForObjectType(objectType: string): RecommendedCatalogSheetDef[] {
  const seen = new Set<string>();
  const out: RecommendedCatalogSheetDef[] = [];
  for (const wf of GENERATED_WORKFLOWS) {
    if (wf.objectType !== objectType) continue;
    for (const phase of wf.phases) {
      const label = String(phase.name ?? '').trim();
      if (!label) continue;
      const norm = label.toLocaleLowerCase('cs');
      if (seen.has(norm)) continue;
      seen.add(norm);
      // phase_key = workflow phase code (same as map stamp / material_usage.phase_code)
      out.push({
        key: phase.code,
        label,
      });
    }
  }
  return out;
}

function buildSheets(): Record<RecommendedCatalogObjectType, readonly RecommendedCatalogSheetDef[]> {
  const vykop = uniquePhaseSheetsForObjectType('VYKOP');
  const jama = uniquePhaseSheetsForObjectType('JAMA');
  const spojkoviste = uniquePhaseSheetsForObjectType('SPOJKOVISTE');

  const budovaPhases = uniquePhaseSheetsForObjectType('BUDOVA');
  const budovaPhaseLabels = new Set(budovaPhases.map((s) => s.label.toLocaleLowerCase('cs')));
  const budovaMilestones: RecommendedCatalogSheetDef[] = [];
  for (const id of BUDOVA_MILESTONE_IDS) {
    const label = BUDOVA_MILESTONE_LABELS[id];
    if (budovaPhaseLabels.has(label.toLocaleLowerCase('cs'))) continue;
    budovaMilestones.push({ key: `BUDOVA.MILESTONE.${id}`, label });
  }

  const rozvadecPhases = uniquePhaseSheetsForObjectType('ROZVADEC');
  const rozvadecPhaseLabels = new Set(rozvadecPhases.map((s) => s.label.toLocaleLowerCase('cs')));
  const rozvadecMilestones: RecommendedCatalogSheetDef[] = [];
  for (const id of ROZVADEC_MILESTONE_IDS) {
    const label = ROZVADEC_MILESTONE_LABELS[id];
    if (rozvadecPhaseLabels.has(label.toLocaleLowerCase('cs'))) continue;
    rozvadecMilestones.push({ key: `ROZVADEC.MILESTONE.${id}`, label });
  }

  return {
    VYKOP: vykop,
    JAMA: jama,
    SPOJKOVISTE: spojkoviste,
    BUDOVA: [...budovaPhases, ...budovaMilestones],
    ROZVADEC: [...rozvadecPhases, ...rozvadecMilestones],
  };
}

/** Listy (fáze/milníky) doporučeného katalogu podle typu objektu. */
export const RECOMMENDED_CATALOG_SHEETS: Record<
  RecommendedCatalogObjectType,
  readonly RecommendedCatalogSheetDef[]
> = buildSheets();

export function isMaterialCatalogAdminScope(
  raw: string | null | undefined,
): raw is MaterialCatalogAdminScope {
  const key = String(raw ?? '')
    .trim()
    .toUpperCase();
  return (MATERIAL_CATALOG_ADMIN_SCOPES as readonly string[]).includes(key);
}

export function isRecommendedCatalogObjectType(
  raw: string | null | undefined,
): raw is RecommendedCatalogObjectType {
  const key = String(raw ?? '')
    .trim()
    .toUpperCase();
  return (RECOMMENDED_CATALOG_OBJECT_TYPES as readonly string[]).includes(key);
}

export function parseMaterialCatalogAdminScope(
  raw: string | null | undefined,
  fallback: MaterialCatalogAdminScope = 'OBECNY',
): MaterialCatalogAdminScope {
  return isMaterialCatalogAdminScope(raw) ? (String(raw).trim().toUpperCase() as MaterialCatalogAdminScope) : fallback;
}

/** Najde definici listu podle klíče, stamp kódu fáze/milníku, nebo českého názvu. */
export function resolveRecommendedCatalogSheet(
  objectType: RecommendedCatalogObjectType,
  sheetNameOrKey: string | null | undefined,
): RecommendedCatalogSheetDef | undefined {
  const raw = String(sheetNameOrKey ?? '').trim();
  if (!raw) return undefined;
  const sheets = RECOMMENDED_CATALOG_SHEETS[objectType];
  const upper = raw.toUpperCase();
  const byKey = sheets.find((s) => s.key.toUpperCase() === upper);
  if (byKey) return byKey;

  const lower = raw.toLocaleLowerCase('cs');
  const byLabel = sheets.find((s) => s.label.toLocaleLowerCase('cs') === lower);
  if (byLabel) return byLabel;

  // Map stamp: workflow phase code → Czech label (BUDOVA.2.PHASE.0 ≈ Nepřipojený)
  const phaseLabel = resolvePhaseLabel(raw, objectType).trim();
  if (phaseLabel && phaseLabel.toLocaleLowerCase('cs') !== lower) {
    const byPhaseLabel = sheets.find(
      (s) => s.label.toLocaleLowerCase('cs') === phaseLabel.toLocaleLowerCase('cs'),
    );
    if (byPhaseLabel) return byPhaseLabel;
  }

  // Legacy keys `TYPE.PHASE.<workflowCode>` from early drafts
  const legacySuffix = `.PHASE.${upper}`;
  const byLegacy = sheets.find((s) => s.key.toUpperCase().endsWith(legacySuffix));
  if (byLegacy) return byLegacy;
  const byEmbedded = sheets.find((s) => {
    const k = s.key.toUpperCase();
    return k.endsWith(`.${upper}`) || k.includes(`.PHASE.${upper}`);
  });
  if (byEmbedded) return byEmbedded;

  // Milestone stamps already use BUDOVA.MILESTONE.* / ROZVADEC.MILESTONE.* (matched by key);
  // also accept bare milestone id.
  if (objectType === 'BUDOVA') {
    const mid =
      parseBudovaMilestoneFromAttachmentPhaseKey(raw) ??
      ((BUDOVA_MILESTONE_IDS as readonly string[]).includes(upper)
        ? (upper as (typeof BUDOVA_MILESTONE_IDS)[number])
        : null);
    if (mid) {
      const key = `BUDOVA.MILESTONE.${mid}`.toUpperCase();
      return sheets.find((s) => s.key.toUpperCase() === key);
    }
  }
  if (objectType === 'ROZVADEC') {
    const mid =
      parseRozvadecMilestoneFromAttachmentPhaseKey(raw) ??
      ((ROZVADEC_MILESTONE_IDS as readonly string[]).includes(upper)
        ? (upper as (typeof ROZVADEC_MILESTONE_IDS)[number])
        : null);
    if (mid) {
      const key = `ROZVADEC.MILESTONE.${mid}`.toUpperCase();
      return sheets.find((s) => s.key.toUpperCase() === key);
    }
  }

  return undefined;
}

/** True when object type has a recommended-materials catalog (not Obecný / STAVAJICI / …). */
export function objectTypeHasRecommendedCatalog(
  objectType: string | null | undefined,
): objectType is RecommendedCatalogObjectType {
  return isRecommendedCatalogObjectType(objectType);
}

const REC_CATEGORY_PATH_SEP = '\u001f';

/** Stabilní UUID kategorie doporučeného katalogu z (typ, fáze, cesta). */
export function recommendedCategoryIdFromPath(
  objectType: RecommendedCatalogObjectType,
  phaseKey: string,
  segments: readonly string[],
): string {
  if (segments.length === 0) {
    throw new Error('Recommended category path must not be empty');
  }
  const key = [
    objectType,
    phaseKey,
    ...segments.map((s) => normalizeProductCategorySegment(s) ?? ''),
  ].join(REC_CATEGORY_PATH_SEP);
  return sha256HexToUuidV4Style(sha256Hex(`recommended-cat:${key}`));
}

/** Výsledek hard-sync importu katalogu (Obecný nebo doporučený). */
export interface MaterialCatalogHardSyncImportResult {
  scope: MaterialCatalogAdminScope;
  /** Unikátní SAP napříč všemi listy. */
  rowCount: number;
  createdCount: number;
  updatedCount: number;
  activatedCount: number;
  /** Obecný: deaktivované SAP mimo soubor. */
  deactivatedCount: number;
  /** Doporučené: odebrané členství (SAP×fáze) mimo soubor. */
  removedRecommendedCount: number;
  duplicateSapCount: number;
  /** Zpracované listy (názvy). */
  sheetNames: string[];
  createdSaps: string[];
  updatedSaps: string[];
  deactivatedSaps: string[];
}
